import { useEffect, useRef } from 'react';
import { InvoiceHire } from '../../types';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';

/**
 * Edit mode: native selects render before their option lists load, so the
 * saved value falls back to the placeholder. Re-apply saved values once the
 * option lists are available (only once per field).
 */
export function useInvoiceHireEditPrefill(
  invoiceHire: InvoiceHire | undefined,
  clients: any[],
  branches: any[],
  currencies: any[],
  setValue: (field: any, value: any) => void
) {
  const appliedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!invoiceHire) return;
    const apply = (field: string, value?: string) => {
      if (!value || appliedRef.current[field]) return;
      setValue(field, value);
      appliedRef.current[field] = true;
    };

    // Saved ids may be numeric while options use documentId — normalize first
    if (clients.length > 0 && invoiceHire.customerId) {
      const c: any = clients.find((cl: any) =>
        cl.documentId === invoiceHire.customerId || String(cl.id) === String(invoiceHire.customerId));
      apply('customerId', c ? (c.documentId || String(c.id)) : String(invoiceHire.customerId));
    }
    if (branches.length > 0 && invoiceHire.vesselId) {
      const b: any = branches.find((br: any) =>
        br.documentId === invoiceHire.vesselId || String(br.id) === String(invoiceHire.vesselId));
      apply('vesselId', b ? (b.documentId || String(b.id)) : String(invoiceHire.vesselId));
    }
    if ((currencies || []).length > 0) {
      apply('currency', resolveCurrencyDocumentId(invoiceHire.currency as any, currencies) || undefined);
      if (!appliedRef.current.itemCurrencies) {
        (invoiceHire.items || []).forEach((item: any, idx: number) => {
          if (item.currency) {
            setValue(`items.${idx}.currency`,
              resolveCurrencyDocumentId(item.currency, currencies) || item.currency);
          }
        });
        appliedRef.current.itemCurrencies = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceHire, clients, branches, currencies]);
}
